import { LOCALE_COMMON_PATHS, LocalePaths } from './locales';

//#region Walk Score Description Type related constants and helper functions

// Walk Score Descriptions (90-100)
export const WALK_SCORE_WALKERS_PARADISE = "Walker's Paradise" as const;
// Walk Score Descriptions (70-89)
export const WALK_SCORE_VERY_WALKABLE = 'Very Walkable' as const;
// Walk Score Descriptions (50-69)
export const WALK_SCORE_SOMEWHAT_WALKABLE = 'Somewhat Walkable' as const;
// Walk Score Descriptions (0-49)
export const WALK_SCORE_CAR_DEPENDENT = 'Car-Dependent' as const;

/** The Walk Score description based on the score range. */
export type WalkScoreDescription = 
  | typeof WALK_SCORE_WALKERS_PARADISE 
  | typeof WALK_SCORE_VERY_WALKABLE 
  | typeof WALK_SCORE_SOMEWHAT_WALKABLE 
  | typeof WALK_SCORE_CAR_DEPENDENT;

/**
 * Maps a Walk Score description to the appropriate localization key.
 * 
 * @param description The Walk Score description from the Walk Score API
 * @returns A LocalePaths key that corresponds to the appropriate translation string
 */
export function getWalkScoreDescriptionLabelKey(description: WalkScoreDescription | string | undefined): LocalePaths {
  switch (description) {
    case WALK_SCORE_WALKERS_PARADISE:
      return LOCALE_COMMON_PATHS.WALK_SCORE_DESCRIPTION_WALK_WALKERS_PARADISE;
    case WALK_SCORE_VERY_WALKABLE:
      return LOCALE_COMMON_PATHS.WALK_SCORE_DESCRIPTION_WALK_VERY_WALKABLE;
    case WALK_SCORE_SOMEWHAT_WALKABLE:
      return LOCALE_COMMON_PATHS.WALK_SCORE_DESCRIPTION_WALK_SOMEWHAT_WALKABLE;
    case WALK_SCORE_CAR_DEPENDENT:
      return LOCALE_COMMON_PATHS.WALK_SCORE_DESCRIPTION_WALK_CAR_DEPENDENT;
    default:
      return description as LocalePaths;
  }
}

//#endregion

//#region Transit Score Description Type related constants and helper functions

// Transit Score Descriptions (90-100)
export const TRANSIT_SCORE_RIDERS_PARADISE = "Rider's Paradise" as const;
// Transit Score Descriptions (70-89)
export const TRANSIT_SCORE_EXCELLENT_TRANSIT = 'Excellent Transit' as const;
// Transit Score Descriptions (50-69)
export const TRANSIT_SCORE_GOOD_TRANSIT = 'Good Transit' as const;
// Transit Score Descriptions (25-49)
export const TRANSIT_SCORE_SOME_TRANSIT = 'Some Transit' as const;
// Transit Score Descriptions (0-24)
export const TRANSIT_SCORE_MINIMAL_TRANSIT = 'Minimal Transit' as const;
// Transit Score Descriptions (0 or unavailable)
export const TRANSIT_SCORE_NO_NEARBY_TRANSIT = 'No Nearby Transit' as const;

/** The Transit Score description based on the score range. */
export type TransitScoreDescription = 
  | typeof TRANSIT_SCORE_RIDERS_PARADISE 
  | typeof TRANSIT_SCORE_EXCELLENT_TRANSIT 
  | typeof TRANSIT_SCORE_GOOD_TRANSIT 
  | typeof TRANSIT_SCORE_SOME_TRANSIT 
  | typeof TRANSIT_SCORE_MINIMAL_TRANSIT
  | typeof TRANSIT_SCORE_NO_NEARBY_TRANSIT;

/**
 * Maps a Transit Score description to the appropriate localization key.
 * 
 * @param description The Transit Score description from the Walk Score API
 * @returns A LocalePaths key that corresponds to the appropriate translation string
 */
export function getTransitScoreDescriptionLabelKey(description: TransitScoreDescription | string | undefined): LocalePaths {
  switch (description) {
    case TRANSIT_SCORE_RIDERS_PARADISE:
      return LOCALE_COMMON_PATHS.WALK_SCORE_DESCRIPTION_TRANSIT_RIDERS_PARADISE;
    case TRANSIT_SCORE_EXCELLENT_TRANSIT:
      return LOCALE_COMMON_PATHS.WALK_SCORE_DESCRIPTION_TRANSIT_EXCELLENT_TRANSIT;
    case TRANSIT_SCORE_GOOD_TRANSIT:
      return LOCALE_COMMON_PATHS.WALK_SCORE_DESCRIPTION_TRANSIT_GOOD_TRANSIT;
    case TRANSIT_SCORE_SOME_TRANSIT:
      return LOCALE_COMMON_PATHS.WALK_SCORE_DESCRIPTION_TRANSIT_SOME_TRANSIT;
    case TRANSIT_SCORE_MINIMAL_TRANSIT:
      return LOCALE_COMMON_PATHS.WALK_SCORE_DESCRIPTION_TRANSIT_MINIMAL_TRANSIT;
    case TRANSIT_SCORE_NO_NEARBY_TRANSIT:
      return LOCALE_COMMON_PATHS.WALK_SCORE_DESCRIPTION_TRANSIT_NO_NEARBY_TRANSIT;
    default:
      return description as LocalePaths;
  }
}

//#endregion

//#region Bike Score Description Type related constants and helper functions

// Bike Score Descriptions (90-100)
export const BIKE_SCORE_BIKERS_PARADISE = "Biker's Paradise" as const;
// Bike Score Descriptions (70-89)
export const BIKE_SCORE_VERY_BIKEABLE = 'Very Bikeable' as const;
// Bike Score Descriptions (50-69)
export const BIKE_SCORE_BIKEABLE = 'Bikeable' as const;
// Bike Score Descriptions (0-49)
export const BIKE_SCORE_SOMEWHAT_BIKEABLE = 'Somewhat Bikeable' as const;

/** The Bike Score description based on the score range. */
export type BikeScoreDescription = 
  | typeof BIKE_SCORE_BIKERS_PARADISE 
  | typeof BIKE_SCORE_VERY_BIKEABLE 
  | typeof BIKE_SCORE_BIKEABLE 
  | typeof BIKE_SCORE_SOMEWHAT_BIKEABLE;

/**
 * Maps a Bike Score description to the appropriate localization key.
 * 
 * @param description The Bike Score description from the Walk Score API
 * @returns A LocalePaths key that corresponds to the appropriate translation string
 */
export function getBikeScoreDescriptionLabelKey(description: BikeScoreDescription | string | undefined): LocalePaths {
  switch (description) {
    case BIKE_SCORE_BIKERS_PARADISE:
      return LOCALE_COMMON_PATHS.WALK_SCORE_DESCRIPTION_BIKE_BIKERS_PARADISE;
    case BIKE_SCORE_VERY_BIKEABLE:
      return LOCALE_COMMON_PATHS.WALK_SCORE_DESCRIPTION_BIKE_VERY_BIKEABLE;
    case BIKE_SCORE_BIKEABLE:
      return LOCALE_COMMON_PATHS.WALK_SCORE_DESCRIPTION_BIKE_BIKEABLE;
    case BIKE_SCORE_SOMEWHAT_BIKEABLE:
      return LOCALE_COMMON_PATHS.WALK_SCORE_DESCRIPTION_BIKE_SOMEWHAT_BIKEABLE;
    default:
      return description as LocalePaths;
  }
}

//#endregion